import { Calendar, CalendarProps, dayjsLocalizer } from "react-big-calendar";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Box, Flex } from "@mantine/core";
import CalendarTabControls from "./CalendarTabControls";
import SideBarForEmployee from "./SideBarForEmployee";
import CalendarFilters from "./CalendarFilters";
import ShiftModal from "./ShiftModal";
import CalendarShiftEvent from "./CalendarShiftEvent";
import { DndContext, DragEndEvent, DragOverlay } from "@dnd-kit/core";
import {
  assignEmployee,
  createWorkShift,
  getAssignShifts,
  getWorkShifts,
  updateShiftById,
} from "../../api/shiftAssignmentApi";
import CalendarShowMore from "./CalendarShowMore";
import { createNotification } from "../../utils/useNotification";
import CalendarShiftEventWeekly from "./CalendarShiftEventWeekly";

const localizer = dayjsLocalizer(dayjs);

const MyCalendar = () => {
  const [employees, setEmployees] = useState<any>([]);

  const [events, setEvents] = useState<any>([]);
  const [openedPopoverDate, setOpenedPopoverDate] = useState<any>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<any>();
  const [selectedEvent, setSelectedEvent] = useState<any>();
  const [selectedDepartment, setSelectedDepartment] = useState();
  const [selectedRole, setSelectedRole] = useState<any>();
  const [date, setDate] = useState<any>(dayjs());
  const [activeTab, setActiveTab] = useState("shift-plan");
  const [view, setView] = useState<any>("month");
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeDraggableId, setActiveDraggableId] = useState<string | null>(
    null
  );

  const handleSelectSlot = useCallback(
    ({ start, end, action }: any) => {
      if (dayjs(start).isBefore(dayjs(), "day") && activeTab == "shift-plan") {
        createNotification({
          success: false,
          message: "Geçmiş zamana vardiya atanamaz.",
        });
        return;
      }
      if (activeTab == "shift-plan" && action == "click") {
        setSelectedDay({ start, end });
        setIsEditMode(false);
        setIsModalOpen(true);
      }
    },
    [setEvents, activeTab]
  );

  const handleSelectEvent = useCallback(
    (event: any) => {
      if (activeTab === "shift-plan") {
        setSelectedEvent(event);
        setIsEditMode(true);
        setIsModalOpen(true);
      }
    },
    [setEvents, activeTab]
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveDraggableId(null);

    if (!over) return;

    const droppedEmployee = employees.find((emp: any) => emp.id === active.id);
    if (!droppedEmployee) return;

    const eventId = over.id;
    const targetEvent = events.find((e: any) => e.id.toString() === eventId);
    if (!targetEvent) return;

    const existingEmployees = Array.isArray(targetEvent.employees)
      ? targetEvent.employees
      : [];

    const alreadyAssigned = existingEmployees.some(
      (emp: any) => emp.id === droppedEmployee.id
    );

    const isCapacityFull =
      targetEvent.count && existingEmployees.length >= targetEvent.count;

    if (alreadyAssigned || isCapacityFull) return;

    try {
      const response = await assignEmployee({
        shiftId: targetEvent.id,
        employees: [droppedEmployee.id],
      });

      if (response?.success) {
        createNotification({
          success: response?.success,
          message: response?.message,
        });
        await getShiftsForEmployeeAssign();
      } else {
        createNotification({
          success: response?.success,
          message: response?.message,
        });
      }
    } catch (error) {}
  };

  const handleDragStart = (event: any) => {
    setActiveDraggableId(event.active.id);
  };

  const activeEmployee = useMemo(() => {
    if (!activeDraggableId) return null;
    return employees.find(
      (emp: { id: string }) => emp.id === activeDraggableId
    );
  }, [activeDraggableId, employees]);

  const getShiftsForShiftPlan = async () => {
    const { data }: any = await getWorkShifts({
      departmentId: selectedDepartment,
      roleId: selectedRole?.value,
      date: dayjs(date).format("YYYY/MM"),
    });

    const formattedShifts = data?.response?.shifts?.map(
      ({ startTime, endTime, count, id, unpaidBreakMinutes, notes }: any) => ({
        id: id,
        count: count,
        startTime: dayjs(startTime).toDate(),
        endTime: dayjs(endTime).toDate(),
        unpaidBreakMinutes: unpaidBreakMinutes,
        notes: notes,
        title: `${dayjs(startTime).format("HH:mm")} - ${dayjs(endTime).format(
          "HH:mm"
        )}`,
      })
    );
    setEvents(formattedShifts);
  };

  const getShiftsForEmployeeAssign = async () => {
    const { data }: any = await getAssignShifts({
      departmentId: selectedDepartment,
      roleId: selectedRole?.value,
      date: dayjs(date).format("YYYY/MM"),
    });

    const formattedShifts = data?.shifts?.map(
      ({
        startTime,
        endTime,
        count,
        id,
        employees,
        unpaidBreakMinutes,
        notes,
      }: any) => ({
        id: id,
        count: count,
        startTime: dayjs(startTime).toDate(),
        endTime: dayjs(endTime).toDate(),
        employees: employees,
        unpaidBreakMinutes: unpaidBreakMinutes,
        notes: notes,
        title: `${dayjs(startTime).format("HH:mm")} - ${dayjs(endTime).format(
          "HH:mm"
        )}`,
      })
    );
    setEvents(formattedShifts);
    setEmployees(data?.employees);
  };

  const createShifts = async ({ shift }: any) => {
    const data: any = await createWorkShift({
      departmentId: selectedDepartment,
      roleId: selectedRole?.value,
      date: dayjs(date).format("YYYY/MM"),
      shift: shift,
    });
    return data;
  };

  const updateShift = async ({ shift }: any) => {
    const data: any = await updateShiftById({
      id: shift.id,
      updates: shift,
    });
    return data;
  };

  useEffect(() => {
    if (selectedDepartment && selectedRole) {
      if (activeTab === "shift-plan") {
        getShiftsForShiftPlan();
      } else {
        getShiftsForEmployeeAssign();
      }
    }
  }, [selectedRole, selectedDepartment, date, activeTab]);

  return (
    <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <Flex justify={"space-between"} mb={24}>
        <CalendarFilters
          view={view}
          setView={setView}
          date={date}
          setDate={setDate}
          selectedDepartment={selectedDepartment}
          setSelectedDepartment={setSelectedDepartment}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
        />
        {selectedDepartment && selectedRole && (
          <CalendarTabControls
            setActiveTab={setActiveTab}
            activeTab={activeTab}
          />
        )}
      </Flex>
      <Box display={"flex"} flex={1} w={"100%"}>
        {selectedDepartment && selectedRole && (
          <>
            <SideBarForEmployee
              employees={employees}
              isVisible={activeTab === "employee-assign"}
            />
            <StyledCalendar
              localizer={localizer}
              startAccessor="startTime"
              endAccessor="endTime"
              style={{ height: "800px", width: "100%", zIndex: 1 }}
              events={events}
              toolbar={false}
              view={view}
              date={date}
              selectable={true}
              formats={{
                weekdayFormat: (date: any, culture: any, localizer: any) =>
                  localizer.format(date, "dddd", culture),
                dayFormat: (date: any, culture: any, localizer: any) =>
                  localizer.format(date, "DD dddd", culture),
              }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              components={{
                month: {
                  event: (event: any) => (
                    <CalendarShiftEvent
                      {...event}
                      setEvents={setEvents}
                      activeTab={activeTab}
                      getShiftsForEmployeeAssign={getShiftsForEmployeeAssign}
                      selectedRole={selectedRole}
                    />
                  ),
                },
                week: {
                  event: (event: any) => (
                    <CalendarShiftEventWeekly
                      {...event}
                      activeTab={activeTab}
                      selectedRole={selectedRole}
                      getShiftsForEmployeeAssign={getShiftsForEmployeeAssign}
                      handleSelectEvent={handleSelectEvent}
                    />
                  ),
                },
                showMore: (event: any) => (
                  <CalendarShowMore
                    {...event}
                    activeTab={activeTab}
                    setEvents={setEvents}
                    setIsModalOpen={setIsModalOpen}
                    setSelectedEvent={setSelectedEvent}
                    selectedRole={selectedRole}
                    opened={openedPopoverDate === event?.slot}
                    onOpen={() => {
                      if (openedPopoverDate !== event?.slot)
                        setOpenedPopoverDate(event?.slot);
                      else {
                        setOpenedPopoverDate(null);
                      }
                    }}
                  />
                ),
              }}
            />
          </>
        )}
      </Box>
      <DragOverlay zIndex={500} dropAnimation={null}>
        {activeEmployee ? (
          <div
            style={{
              height: "52px",
              width: "188px",
              display: "flex",
              alignItems: "center",
              border: "1px dashed #5C26FF",
              justifyContent: "center",
              cursor: "grab",
              backgroundColor: "rgba(255,255,255,0.6)",
            }}
          >
            {activeEmployee?.name}
          </div>
        ) : null}
      </DragOverlay>
      <ShiftModal
        isOpen={isModalOpen}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onClose={() => {
          setIsModalOpen(false);
        }}
        selectedDay={selectedDay}
        selectedEvent={selectedEvent}
        selectedRole={selectedRole}
        getShiftsForShiftPLan={getShiftsForShiftPlan}
        onAddEvent={async ({ newEvent, isEditMode }: any) => {
          if (isEditMode) {
            const response = await updateShift({ shift: newEvent });
            if (response?.success) {
              await getShiftsForShiftPlan();
              createNotification({
                success: response?.success,
                message: response?.message,
              });
            } else {
              createNotification({
                success: response?.success,
                message: response?.message,
              });
            }
          } else {
            const response = await createShifts({ shift: newEvent });
            if (response?.success) {
              await getShiftsForShiftPlan();
              createNotification({
                success: response?.success,
                message: response?.message,
              });
            } else {
              createNotification({
                success: response?.success,
                message: response?.message,
              });
            }
          }
        }}
      />
    </DndContext>
  );
};

export default MyCalendar;

const StyledCalendar = styled(Calendar)<CalendarProps | any>`
  .rbc-header {
    display: flex;
    justify-content: end;
    align-items: center;
    padding-right: 5px;
    height: 44px;
    background-color: #eef2ff;
    span {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: #111827;
    }
  }
  .rbc-header:first-of-type {
    border-top-left-radius: 4px;
  }

  .rbc-header:last-of-type {
    border-top-right-radius: 4px;
  }
  .rbc-button-link {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
  .rbc-event {
    background: unset;
    box-sizing: unset;
    padding: unset;
    border-color: unset;
    &:focus {
      outline: unset;
    }
  }
  .rbc-event-label {
    display: none;
  }
  .rbc-date-cell {
    position: relative;
  }
  .rbc-row-segment {
    padding: 0px 1px 4px 1px;
  }
  .rbc-month-row {
    overflow: visible;
  }
  .rbc-today {
    background-color: unset;
  }
  .rbc-row {
    min-height: unset;
  }
  .rbc-month-view {
    border-radius: 4px;
  }
`;
